/**
 * Reloj corregido de la caja offline.
 *
 * Lo que se cobra sin red viaja con la hora del dispositivo y el servidor la respeta
 * (ver `OfflineTimestamp` en el backend). Un reloj atrasado no se puede detectar allá:
 * una hora vieja legítima y un reloj mal puesto se ven igual. Así que aquí, mientras
 * hay red, se mide la diferencia contra la hora del servidor —la cabecera `Date` de
 * cualquier respuesta— y se le aplica a todo lo que se guarda en la cola.
 *
 * El desfase se guarda en localStorage para que siga puesto al abrir la app sin red,
 * que es justo cuando hace falta.
 */
const OFFSET_KEY = 'offlineClockOffsetMs';

/** Desfases absurdos (> 12 h) no se aplican: un reloj así delata otro problema. */
const MAX_OFFSET_MS = 12 * 60 * 60 * 1000;

let offsetMs: number | null = null;

const readStoredOffset = (): number => {
  if (offsetMs !== null) return offsetMs;
  try {
    const stored = Number(localStorage.getItem(OFFSET_KEY));
    offsetMs = Number.isFinite(stored) ? stored : 0;
  } catch {
    offsetMs = 0;
  }
  return offsetMs;
};

/**
 * Lo llama baseApi con la cabecera `Date` de cada respuesta. La cabecera tiene
 * resolución de segundos y no descuenta el viaje de la petición, así que el desfase
 * queda con un error de un par de segundos: suficiente para lo que importa (que un
 * cobro no caiga en otro día ni antes de que se abriera la caja), y por eso tampoco
 * se corrige un desfase menor a un minuto.
 */
export const syncClockFromServerDate = (dateHeader: string | null | undefined): void => {
  if (!dateHeader) return;
  const serverTime = Date.parse(dateHeader);
  if (!Number.isFinite(serverTime)) return;

  const diff = serverTime - Date.now();
  const next = Math.abs(diff) < 60_000 || Math.abs(diff) > MAX_OFFSET_MS ? 0 : diff;

  if (next === readStoredOffset()) return;
  offsetMs = next;
  try {
    localStorage.setItem(OFFSET_KEY, String(next));
  } catch {
    // Sin localStorage el desfase solo vive en esta pestaña.
  }
};

/** Hora actual corregida, en UTC ISO: es la que viaja como `occurredAt`. */
export const offlineNowIso = (): string => new Date(Date.now() + readStoredOffset()).toISOString();

/** Solo para diagnóstico y pruebas. */
export const getClockOffsetMs = (): number => readStoredOffset();

export const resetClockOffsetForTests = (): void => {
  offsetMs = null;
  try {
    localStorage.removeItem(OFFSET_KEY);
  } catch {
    // ignorado
  }
};
